import React, { useState, useEffect, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  Box,
  Grid,
  Typography,
  Pagination,
  Stack,
  FormControlLabel,
  Switch,
} from "@mui/material";

import CampaignIcon from "@mui/icons-material/Campaign";

import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import AppButton from "../components/buttons/AppButton";
import ConfirmDialog from "../components/common/ConfirmDialog";

import AnnouncementCard from "../components/announcements/AnnouncementCard";
import CreateAnnouncementModal from "../components/announcements/CreateAnnouncementModal";
import UpdateAnnouncementModal from "../components/announcements/UpdateAnnouncementModal";
import ViewAnnouncementModal from "../components/announcements/ViewAnnouncementModal";
import AppCircularProgress from "../components/common/AppCircularProgress";

import useDebounce from "../hooks/useDebounce";
import { usePermissions } from "../hooks/usePermissions";

import {
  getAnnouncements,
  getAnnouncementById,
  deleteAnnouncement,
  resetAnnouncementServerStatus,
} from "../redux/actions/announcementActions";
import PageError from "../components/common/PageError";
import { AnnouncementSummaryDto } from "../shared/dtos/announcement/AnnouncementSummaryDto";

const AnnouncementsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  // --- Redux State ---
  const {
    items: announcements,
    item: announcement,
    pagination,
    loadingFetchAll: loadingAnnouncements,
    loadingFetchOne: loadingAnnouncement,
    errorFetchAll: errorLoadingAnnouncements,
    errorFetchOne: errorLoadingAnnouncement,
    serverStatus,
    loadingDelete: loadingAnnouncementDelete,
    loadingUpdate: loadingAnnouncementUpdate,
    loadingCreate: loadingAnnouncementCreate,
    errorCreate: errorCreatingAnnouncement,
    errorUpdate: errorUpdatingAnnouncement,
  } = useAppSelector((state) => state.announcement);

  // --- Local State ---
  const [pageConfig, setPageConfig] = useState({ page: 1, limit: 12 });
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearch = useDebounce<string>(searchTerm, 500);
  const [includeExpired, setIncludeExpired] = useState<boolean>(false);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState<boolean>(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] =
    useState<AnnouncementSummaryDto | null>(null);

  // --- Permissions ---
  const canCreate = hasPermission("announcement:create");
  const canUpdate = hasPermission("announcement:update");
  const canDelete = hasPermission("announcement:delete");
  const canRead = hasPermission("announcement:read");

  // --- Data Fetching ---
  const fetchAnnouncementsList = useCallback(() => {
    dispatch(
      getAnnouncements({
        page: pageConfig.page,
        size: pageConfig.limit,
        search: debouncedSearch,
        includeExpired,
      }),
    );
  }, [
    dispatch,
    pageConfig.page,
    pageConfig.limit,
    debouncedSearch,
    includeExpired,
  ]);

  useEffect(() => {
    fetchAnnouncementsList();
  }, [fetchAnnouncementsList]);

  // --- Modal Handlers ---
  const handleOpenCreate = (): void => setIsCreateModalOpen(true);
  const handleCloseCreate = useCallback(() => {
    if (!loadingAnnouncementCreate) setIsCreateModalOpen(false);
  }, [loadingAnnouncementCreate]);

  const handleOpenUpdate = (item: AnnouncementSummaryDto): void => {
    dispatch(getAnnouncementById(item.id)); // Fetch full details (with classes/grades) before editing
    setSelectedItem(item);
    setIsUpdateModalOpen(true);
  };
  const handleCloseUpdate = useCallback(() => {
    if (!loadingAnnouncementUpdate) {
      setIsUpdateModalOpen(false);
      setSelectedItem(null);
    }
  }, [loadingAnnouncementUpdate]);

  const handleOpenDelete = (item: AnnouncementSummaryDto): void => {
    setSelectedItem(item);
    setIsDeleteModalOpen(true);
  };
  const handleCloseDelete = useCallback(() => {
    if (!loadingAnnouncementDelete) {
      setIsDeleteModalOpen(false);
      setSelectedItem(null);
    }
  }, [loadingAnnouncementDelete]);

  const handleOpenView = (item: AnnouncementSummaryDto): void => {
    dispatch(getAnnouncementById(item.id)); // Fetches full target arrays
    setIsViewModalOpen(true);
  };
  const handleCloseView = (): void => setIsViewModalOpen(false);

  const handleDeleteConfirm = (): void => {
    if (selectedItem) dispatch(deleteAnnouncement(selectedItem.id));
  };

  // Auto-close modals on success
  useEffect(() => {
    if (!serverStatus) return;
    const { operation, status } = serverStatus;

    if (operation === "create" && status === 201) handleCloseCreate();
    if (operation === "update" && status === 200) handleCloseUpdate();
    if (operation === "delete" && status === 200) handleCloseDelete();

    dispatch(resetAnnouncementServerStatus());
  }, [
    serverStatus,
    dispatch,
    handleCloseCreate,
    handleCloseUpdate,
    handleCloseDelete,
  ]);

  const renderContent = (): React.JSX.Element => {
    if (loadingAnnouncements) return <AppCircularProgress />;

    if (errorLoadingAnnouncements)
      return (
        <PageError
          error={errorLoadingAnnouncements}
          onRetry={fetchAnnouncementsList}
        />
      );

    if (!announcements || announcements.length === 0)
      return (
        <Box sx={{ textAlign: "center", mt: 5 }}>
          <Typography>No Announcements Found</Typography>
        </Box>
      );

    return (
      <>
        <Grid container spacing={3}>
          {announcements.map((item) => (
            <Grid key={item.id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <AnnouncementCard
                announcement={item}
                onView={canRead ? () => handleOpenView(item) : undefined}
                onEdit={canUpdate ? () => handleOpenUpdate(item) : undefined}
                onDelete={canDelete ? () => handleOpenDelete(item) : undefined}
              />
            </Grid>
          ))}
        </Grid>

        {/* Pagination Control */}
        {pagination && pagination.totalPages! > 1 && (
          <Stack sx={{ alignItems: "center", mt: 4 }}>
            <Pagination
              count={pagination.totalPages}
              page={pageConfig.page}
              onChange={(_, value) =>
                setPageConfig((prev) => ({ ...prev, page: value }))
              }
              color="secondary"
              showFirstButton
              showLastButton
            />
          </Stack>
        )}
      </>
    );
  };

  return (
    <PageContainer
      title="Announcements Hub"
      subtitle="Publish and manage school-wide and targeted communications."
      actions={
        canCreate && (
          <AppButton
            variant="contained"
            startIcon={<CampaignIcon />}
            onClick={handleOpenCreate}
          >
            New Announcement
          </AppButton>
        )
      }
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => {
          setSearchTerm(e.target.value);
          setPageConfig((prev) => ({ ...prev, page: 1 }));
        }}
        searchPlaceholder="Search announcements..."
      >
        <FormControlLabel
          control={
            <Switch
              checked={includeExpired}
              onChange={(e) => {
                setIncludeExpired(e.target.checked);
                setPageConfig((prev) => ({ ...prev, page: 1 }));
              }}
              color="secondary"
            />
          }
          label="Include Expired"
          sx={{ ml: 1 }}
        />
      </PageToolbar>

      {renderContent()}

      {/* --- MODALS --- */}
      {isCreateModalOpen && (
        <CreateAnnouncementModal
          open={isCreateModalOpen}
          onClose={handleCloseCreate}
          //userInfo={userInfo}
          loading={loadingAnnouncementCreate}
          error={errorCreatingAnnouncement}
        />
      )}

      {isUpdateModalOpen && selectedItem && (
        <UpdateAnnouncementModal
          open={isUpdateModalOpen}
          onClose={handleCloseUpdate}
          //userInfo={userInfo}
          // Use loaded details if available, otherwise fallback to row
          announcement={
            announcement?.id === selectedItem.id ? announcement : null
          }
          loading={loadingAnnouncementUpdate || loadingAnnouncement}
          error={errorUpdatingAnnouncement || errorLoadingAnnouncement}
        />
      )}

      {isViewModalOpen && (
        <ViewAnnouncementModal
          open={isViewModalOpen}
          onClose={handleCloseView}
          announcement={announcement}
          loading={loadingAnnouncement}
          error={errorLoadingAnnouncement}
        />
      )}

      {isDeleteModalOpen && selectedItem && (
        <ConfirmDialog
          open={isDeleteModalOpen}
          onClose={handleCloseDelete}
          onConfirm={handleDeleteConfirm}
          title="Delete Announcement"
          description={`Are you sure you want to delete the announcement "${selectedItem.title}"?`}
          loading={loadingAnnouncementDelete}
        />
      )}
    </PageContainer>
  );
};

export default AnnouncementsPage;
